import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { CheckboxField } from '@/components/form/fields/checkbox/Checkbox';
import { TextAndIconField } from '@/components/form/fields/text/TextAndIconInput';
import { TextField } from '@/components/form/fields/text/TextInput';
import { SelectInput } from '@/components/form/fields/select/SelectInput';
import { CategoryIcons } from '@/components/icons/CategoryIcons';
import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useMemo, useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { Form } from '../../../components/form/Form';
import { useQueryClient } from '@tanstack/react-query';
import { Modal } from '../../../components/modal/Modal';
import { ModalProps } from '../../../components/modal/useModal';
import { Card } from '../../../components/sections/Card';
import { useCategories, useUpdateCategory, useGetRateCards } from '../../../services/api/hooks/catalog';
import TenantRestrictionTree from '@/components/org/TenantRestrictionTree';
import { useProcessOrgTreeForTenantRestrictions } from '@/components/org/useProcessOrgTreeForTenantRestrictions';
import { ToggleInput } from '@/components/form/fields/toggle/Toggle';
import { useRegions } from '@/services/api/hooks/region';
import { useDirtyFormGuard } from '@/hooks/useDirtyFormGuard';

const DirectionalLogisticsFields = z.object({
  averageWeightKg: z.coerce.number().optional(),
  dimensionalWeightKg: z.coerce.number().optional(),
  packingEfficiencyPercent: z.coerce.number().optional(),
  overrideRateCardId: z.string().optional(),
  itemProcessingRateCardId: z.string().optional()
});

const EditCategoryFormFields = z.object({
  name: z.object({
    text: z.string(),
    iconId: z.string()
  }),
  isMainDeviceCategory: z.boolean(),
  isSerialized: z.boolean().optional(),
  hideFromGenerics: z.boolean().optional(),
  deploy: DirectionalLogisticsFields.optional(),
  return: DirectionalLogisticsFields.optional(),
  regionOverrides: z.array(z.object({
    id: z.string().optional(),
    regionId: z.string(),
    deployOverrideRateCardId: z.string().optional(),
    deployItemProcessingRateCardId: z.string().optional(),
    returnOverrideRateCardId: z.string().optional(),
    returnItemProcessingRateCardId: z.string().optional()
  })).optional()
}).superRefine((data, ctx) => {
  if (!data.hideFromGenerics) {
    if (data.deploy && (data.deploy.averageWeightKg || data.deploy.dimensionalWeightKg)) {
      if (data.deploy.packingEfficiencyPercent === undefined || data.deploy.packingEfficiencyPercent === null || data.deploy.packingEfficiencyPercent < 1 || data.deploy.packingEfficiencyPercent > 100) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Packing Efficiency is required (1-100) for physical categories',
          path: ['deploy', 'packingEfficiencyPercent']
        });
      }
    }
    if (data.return && (data.return.averageWeightKg || data.return.dimensionalWeightKg)) {
      if (data.return.packingEfficiencyPercent === undefined || data.return.packingEfficiencyPercent === null || data.return.packingEfficiencyPercent < 1 || data.return.packingEfficiencyPercent > 100) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Packing Efficiency is required (1-100) for physical categories',
          path: ['return', 'packingEfficiencyPercent']
        });
      }
    }
  }
});

type EditCategoryFormFields = z.infer<typeof EditCategoryFormFields>;

export const EditCategoryModal: FC<ModalProps<{ categoryId: string }>> = props => {
  const EditCategory = () => {
    const [categories] = useCategories();
    const categoryId = props.props?.categoryId;
    if (!categoryId) throw new Error('Missing categoryId');
    const category = categories?.find(category => category.id === categoryId);
    
    const [rateCardsResp, { isLoading: isRateCardsLoading }] = useGetRateCards();
    const overrideRateCards = useMemo(() => {
      if (isRateCardsLoading) return [];
      return rateCardsResp?.items?.filter(r => r.propertyBag?.isExclusiveOverride === true || String(r.propertyBag?.isExclusiveOverride) === 'true').map(r => ({ id: r.id, name: r.name, regionId: r.region?.id })) || [];
    }, [rateCardsResp, isRateCardsLoading]);

    const itemProcessingRateCards = useMemo(() => {
      if (isRateCardsLoading) return [];
      return rateCardsResp?.items?.filter(r => r.propertyBag?.isItemRateOverride === true || String(r.propertyBag?.isItemRateOverride) === 'true').map(r => ({ id: r.id, name: r.name, regionId: r.region?.id })) || [];
    }, [rateCardsResp, isRateCardsLoading]);

    const deployFallback = useMemo(() => {
      const defaultCard = rateCardsResp?.items?.find(r => r.propertyBag?.itemRateForRequestType === 'DeployInventory');
      return { id: '', name: defaultCard ? `Default (${defaultCard.name})` : 'Default' };
    }, [rateCardsResp]);

    const returnFallback = useMemo(() => {
      const defaultCard = rateCardsResp?.items?.find(r => r.propertyBag?.itemRateForRequestType === 'OffboardUser');
      return { id: '', name: defaultCard ? `Default (${defaultCard.name})` : 'Default' };
    }, [rateCardsResp]);

    const methods = useForm({
      resolver: zodResolver(EditCategoryFormFields),
      defaultValues: {
        name: { text: category?.name, iconId: category?.description },
        isMainDeviceCategory: category?.isMainDevice,
        isSerialized: category?.isSerialized,
        hideFromGenerics: category?.hideFromGenerics,
        deploy: category?.logisticsConfig?.deploy,
        return: category?.logisticsConfig?.return,
        regionOverrides: category?.regionOverrides || []
      }
    });

    useDirtyFormGuard(methods.formState.isDirty);
    const [regions] = useRegions();

    const orgTree = useProcessOrgTreeForTenantRestrictions(category?.hiddenFromTenantIds);
    
    const isCurrentlyInternal = category?.hiddenFromTenantIds?.includes("00000000-0000-0000-0000-000000000000") ?? false;
    const [isInternalOnly, setIsInternalOnly] = useState(isCurrentlyInternal);
    
    useEffect(() => {
      setIsInternalOnly(isCurrentlyInternal);
    }, [isCurrentlyInternal]);

    const updateCategory = useUpdateCategory();
    const queryClient = useQueryClient();

    const onSubmit = async (data: EditCategoryFormFields) => {
      const hiddenFromTenantIds = isInternalOnly
        ? ["00000000-0000-0000-0000-000000000000"]
        : orgTree.reduce((ids: string[], msp) => {
            if (msp.checked) {
              ids.push(msp.mspId);
            }
            msp.customers.forEach(customer => {
              if (customer.checked) {
                ids.push(customer.customerId);
              }
            });
            return ids;
          }, []);

      await updateCategory.mutateAsync({
        id: categoryId,
        name: data.name.text,
        description: data.name.iconId,
        isMainDevice: data.isMainDeviceCategory,
        hiddenFromTenantIds,
        isSerialized: data.isSerialized || false,
        hideFromGenerics: data.hideFromGenerics || false,
        regionOverrides: data.regionOverrides,
        logisticsConfig: {
          deploy: data.deploy,
          return: data.return
        }
      }, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['getCategories'] });
          queryClient.invalidateQueries({ queryKey: ['getBundles'] });
        }
      });
      toast.success(`${data.name.text} updated`);
      props.close();
    };

    return (
      <Card className="w-full p-8 max-h-[90vh] overflow-y-auto">
        <Modal.Controls {...props}>
          <h1 className="text-2xl font-semibold">Edit Category</h1>
        </Modal.Controls>
        <Form className="flex w-full flex-col gap-3 py-3" methods={methods} onSubmit={onSubmit}>
          <TextAndIconField id="name" label="Name" icons={CategoryIcons} />
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
            <Controller
              name="isMainDeviceCategory"
              control={methods.control}
              render={({ field }) => (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={field.value || false} onChange={e => field.onChange(e.target.checked)} className="w-4 h-4 text-primary-700" />
                  <span className="text-sm font-medium text-gray-700">Main Device Category</span>
                </label>
              )}
            />
            <Controller
              name="hideFromGenerics"
              control={methods.control}
              render={({ field }) => (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={field.value || false} onChange={e => field.onChange(e.target.checked)} className="w-4 h-4 text-primary-700" />
                  <span className="text-sm font-medium text-gray-700">Hide from Generic Returns List</span>
                </label>
              )}
            />
            <Controller
              name="isSerialized"
              control={methods.control}
              render={({ field }) => (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={field.value || false} onChange={e => field.onChange(e.target.checked)} className="w-4 h-4 text-primary-700" />
                  <span className="text-sm font-medium text-gray-700">Serialised Return Required</span>
                </label>
              )}
            />
          </div>
          
          <div className="space-y-4 mt-4 border-t pt-4">
            <h2 className="text-lg font-medium">Deployment Logistics</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <TextField id="deploy.averageWeightKg" label="Average Weight (kg)" onlyDigits={true} />
              <TextField id="deploy.dimensionalWeightKg" label="Dimensional Weight (kg)" onlyDigits={true} />
              <TextField id="deploy.packingEfficiencyPercent" label="Packing Efficiency (%)" onlyDigits={true} />
              {/* <Controller
                name="deploy.overrideRateCardId"
                control={methods.control}
                render={({ field }) => (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Override Logistics With (Optional)</label>
                    <SelectInput
                      id="deploy.overrideRateCardId"
                      options={[{ id: '', name: 'None' }, ...overrideRateCards]}
                      placeholder="None"
                      value={overrideRateCards.find(r => r.id === field.value) || null}
                      onChange={(val: any) => field.onChange(val?.id || undefined)}
                    />
                  </div>
                )}
              /> */}
              {/* <Controller
                name="deploy.itemProcessingRateCardId"
                control={methods.control}
                render={({ field }) => (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Deployment Processing Rate Card (Optional)</label>
                    <SelectInput
                      id="deploy.itemProcessingRateCardId"
                      options={[deployFallback, ...itemProcessingRateCards]}
                      placeholder={deployFallback.name}
                      value={itemProcessingRateCards.find(r => r.id === field.value) || null}
                      onChange={(val: any) => field.onChange(val?.id || undefined)}
                    />
                  </div>
                )}
              /> */}
            </div>
          </div>

          <div className="space-y-4 mt-4 border-t pt-4">
            <h2 className="text-lg font-medium">Return Logistics</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <TextField id="return.averageWeightKg" label="Average Weight (kg)" onlyDigits={true} />
              <TextField id="return.dimensionalWeightKg" label="Dimensional Weight (kg)" onlyDigits={true} />
              <TextField id="return.packingEfficiencyPercent" label="Packing Efficiency (%)" onlyDigits={true} />
              {/* <Controller
                name="return.overrideRateCardId"
                control={methods.control}
                render={({ field }) => (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Override Logistics With (Optional)</label>
                    <SelectInput
                      id="return.overrideRateCardId"
                      options={[{ id: '', name: 'None' }, ...overrideRateCards]}
                      placeholder="None"
                      value={overrideRateCards.find(r => r.id === field.value) || null}
                      onChange={(val: any) => field.onChange(val?.id || undefined)}
                    />
                  </div>
                )}
              /> */}
              {/* <Controller
                name="return.itemProcessingRateCardId"
                control={methods.control}
                render={({ field }) => (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Return Processing Rate Card (Optional)</label>
                    <SelectInput
                      id="return.itemProcessingRateCardId"
                      options={[returnFallback, ...itemProcessingRateCards]}
                      placeholder={returnFallback.name}
                      value={itemProcessingRateCards.find(r => r.id === field.value) || null}
                      onChange={(val: any) => field.onChange(val?.id || undefined)}
                    />
                  </div>
                )}
              /> */}
            </div>
          </div>
            <div className="space-y-4 border-t pt-4">
              <h2 className="text-lg font-medium">Region Overrides</h2>
              <div className="overflow-visible">
                <table className="w-full text-sm text-left">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="p-2 font-medium">Region</th>
                      <th className="p-2 font-medium">Deploy Crate Fee</th>
                      <th className="p-2 font-medium">Deploy Processing</th>
                      <th className="p-2 font-medium">Return Crate Fee</th>
                      <th className="p-2 font-medium">Return Processing</th>
                    </tr>
                  </thead>
                  <tbody>
                    {regions?.map((region: any) => {
                      const overrides = methods.watch('regionOverrides') || [];
                      const override = overrides.find(ro => ro.regionId === region.id) || { regionId: region.id } as any;
                      const setOverride = (field: string, val: string | undefined) => {
                         const current = methods.getValues('regionOverrides') || [];
                         const existing = current.find(ro => ro.regionId === region.id);
                         let updated;
                         if (existing) {
                           updated = current.map(ro => ro.regionId === region.id ? { ...ro, [field]: val } : ro);
                         } else {
                           updated = [...current, { regionId: region.id, [field]: val }];
                         }
                         methods.setValue('regionOverrides', updated, { shouldDirty: true });
                      };
                      
                      const filteredOverrideCards = overrideRateCards.filter(r => r.regionId === region.id);
                      const filteredProcessingCards = itemProcessingRateCards.filter(r => r.regionId === region.id);
                      
                      return (
                        <tr key={region.id} className="border-b">
                          <td className="p-2 whitespace-nowrap">{region.name}</td>
                          <td className="p-2">
                             <SelectInput
                               id={`ro-deploy-crate-${region.id}`}
                               options={[{ id: '', name: 'Default' }, ...filteredOverrideCards]}
                               placeholder="Default"
                               value={filteredOverrideCards.find(r => r.id === override.deployOverrideRateCardId) || null}
                               onChange={(val: any) => setOverride('deployOverrideRateCardId', val?.id || undefined)}
                             />
                          </td>
                          <td className="p-2">
                             <SelectInput
                               id={`ro-deploy-proc-${region.id}`}
                               options={[{ id: '', name: 'Default' }, ...filteredProcessingCards]}
                               placeholder="Default"
                               value={filteredProcessingCards.find(r => r.id === override.deployItemProcessingRateCardId) || null}
                               onChange={(val: any) => setOverride('deployItemProcessingRateCardId', val?.id || undefined)}
                             />
                          </td>
                          <td className="p-2">
                             <SelectInput
                               id={`ro-return-crate-${region.id}`}
                               options={[{ id: '', name: 'Default' }, ...filteredOverrideCards]}
                               placeholder="Default"
                               value={filteredOverrideCards.find(r => r.id === override.returnOverrideRateCardId) || null}
                               onChange={(val: any) => setOverride('returnOverrideRateCardId', val?.id || undefined)}
                             />
                          </td>
                          <td className="p-2">
                             <SelectInput
                               id={`ro-return-proc-${region.id}`}
                               options={[{ id: '', name: 'Default' }, ...filteredProcessingCards]}
                               placeholder="Default"
                               value={filteredProcessingCards.find(r => r.id === override.returnItemProcessingRateCardId) || null}
                               onChange={(val: any) => setOverride('returnItemProcessingRateCardId', val?.id || undefined)}
                             />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>


            <div className="space-y-1 border-t pt-4">
              <h2 className="text-lg font-medium">Hide from Tenants</h2>
            <div className="flex items-center gap-3 py-2">
              <ToggleInput
                id="isInternalOnly"
                value={isInternalOnly}
                onChange={setIsInternalOnly}
              />
              <span className="text-sm font-medium text-gray-700">Internal Only / Hide from all tenants</span>
            </div>
            {!isInternalOnly && (
              <>
                <p className="text-sm text-gray-500">Select MSPs or Customers that should NOT see this category.</p>
                <TenantRestrictionTree data={orgTree} />
              </>
            )}
          </div>

          <Button className="w-fit place-self-end mt-4" variant={ButtonVariant.Primary} type="submit">
            Update
          </Button>
        </Form>
      </Card>
    );
  };

  return (
    <Modal className="md:max-w-5xl" {...props}>
      <EditCategory />
    </Modal>
  );
};


